import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { env } from "../../../config/environment";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { swallowedFailure, swallowedMessage } from "shared/loadOrError";

/** The masked settings record returned by the read-ai-settings service. */
export interface ReadAiSettingsRecord {
  id: number;
  /** 'auto' | 'anthropic' | 'gemini' | 'azureOpenAI' — the VENDOR preference,
   *  used only when no provider row is chosen. */
  llmProvider: string | null;
  /** The ONE Admin → LLM Providers row meeting work runs on, or null for the
   *  vendor preference above. */
  llmProviderId: string | null;
  /** Why the chosen row cannot run right now (disabled, deleted, no key, no
   *  model), or null. Rendered beside the picker so the fix is named. */
  llmProviderIssue?: string | null;
  anthropicModel: string | null;
  geminiModel: string | null;
  maxTokens: number | null;
  hasAnthropicApiKey: boolean;
  hasGeminiApiKey: boolean;
  hasWebhookSecret: boolean;
  /** Work-item TYPE only ('Task', …). The ADO TARGET is never a default —
   *  routing is per module via module_catalog.adoProject. */
  defaultWorkItemType: string | null;
  /** RETIRED 2026-09-18 — still stored and still editable, read by nothing.
   *  The API warns at boot when a value is set. */
  autoApproveThreshold: number | null;
  llmConfigured: boolean;
  /** Who analyses meetings. 'gantry' = HQ makes no meeting LLM calls. Optional
   *  so a record from an API that predates the field reads as the old pipeline. */
  analysisAuthority?: "gantry" | "hq";
  webhookPath: string;
  webhookSignatureHeader?: string;
  updatedAt?: string;
  /** Result of the `test` action (LLM connectivity ping). */
  test?: { ok: boolean; message?: string };
  /** Result of the `testWebhook` action (signing-key self-check). */
  webhookTest?: { ok: boolean; message?: string };
}

/** Editable form shape. Secrets are write-only (never seeded from the record). */
export interface ReadAiSettingsForm {
  llmProvider: string;
  /** An `llm-providers` id, or "" for the vendor preference in `llmProvider`. */
  llmProviderId: string;
  anthropicApiKey: string;
  anthropicModel: string;
  geminiApiKey: string;
  geminiModel: string;
  maxTokens: string;
  webhookSecret: string;
  defaultWorkItemType: string;
  autoApproveThreshold: string;
}

/** One configured row from Admin → LLM Providers, as the provider picker lists it. */
export interface LlmProviderOption {
  id: string;
  name: string;
  type: string;
  enabled: boolean;
  defaultModel: string | null;
}

/** One Azure DevOps project as the internal connection reports it. */
export interface AdoProjectOption {
  id: string;
  name: string;
}

/** Human labels for the vendor types the LLM domain knows. */
export const VENDOR_LABEL: Record<string, string> = {
  anthropic: "Anthropic (Claude)",
  gemini: "Google Gemini",
  azureOpenAI: "Azure OpenAI",
  openai: "OpenAI",
  ollama: "Ollama (local)",
};

/** The vendor preferences the settings row accepts (mirrors the API's ALLOWED_LLM_PROVIDERS). */
const VENDOR_PREFERENCES = new Set(["auto", "anthropic", "gemini", "azureOpenAI"]);

/** Picker value prefix for a configured provider row (vs. a bare vendor preference). */
const ROW_PREFIX = "row:";

/** The rows of a ServiceStore find() result, whichever shape the wrapper hands back. */
function rowsOf<T>(result: unknown): T[] {
  if (Array.isArray(result)) return result as T[];
  const data = (result as { data?: unknown } | null | undefined)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

/** One governed module row in the per-module ADO routing editor. */
export interface ModuleAdoMapping {
  id: number | string;
  moduleKey: string;
  displayName: string;
  adoProject: string | null;
}

/** The read-ai-oauth service's status record. */
interface RawWriteService {
  create(data: object): Promise<unknown>;
  patch(id: string | number, data: object): Promise<unknown>;
}

export interface ReadAiOauthStatus {
  connected: boolean;
  needsReauth: boolean;
  pendingHandshake: boolean;
  connectedAt: string | null;
  refreshedAt: string | null;
  refreshFailedAt: string | null;
  scope: string | null;
}

const EMPTY_FORM: ReadAiSettingsForm = {
  llmProvider: "auto",
  llmProviderId: "",
  anthropicApiKey: "",
  anthropicModel: "",
  geminiApiKey: "",
  geminiModel: "",
  maxTokens: "",
  webhookSecret: "",
  defaultWorkItemType: "",
  autoApproveThreshold: "",
};

/**
 * Route ViewStore for the Meeting Intelligence Settings page — the admin surface
 * for the whole Read.ai → Language Model → Azure DevOps pipeline config (LLM
 * provider/keys/models, the Read.ai webhook HMAC secret, default Azure DevOps
 * work-item targets, and the auto-approve confidence dial) that used to live in
 * env vars.
 *
 * Backed by the registered `read-ai-settings` ServiceStore (a singleton record,
 * id "1"; see serviceStores/meetingIntelligence.stores.ts) and the
 * `devops-connections` store for the connection picker. The settings record is
 * a single get/patch so it is held locally; the connection options are a
 * computed view over the devops-connections entity cache.
 *
 * Secrets are WRITE-ONLY: the API returns only `hasAnthropicApiKey` /
 * `hasGeminiApiKey` / `hasWebhookSecret` booleans, the form never seeds the
 * secret fields, and a blank value on save keeps the stored secret (a non-blank
 * value rotates it).
 */
class ReadAiSettingsStore {
  rootStore: RootStore;

  settings: ReadAiSettingsRecord | null = null;
  form: ReadAiSettingsForm = { ...EMPTY_FORM };

  loading = false;
  saving = false;
  testing = false;
  testingWebhook = false;
  error: string | null = null;
  formError: string | null = null;
  savedAt: string | null = null;
  testResult: { ok: boolean; message?: string } | null = null;
  webhookTestResult: { ok: boolean; message?: string } | null = null;

  // ── Read.ai OAuth connect (the production path — no shell) ────────────────
  oauthStatus: ReadAiOauthStatus | null = null;
  /** The authorize URL a start() minted; shown until finish() consumes it. */
  authorizeUrl: string | null = null;
  /** The operator's pasted redirect URL (the page that failed to load). */
  finishUrl = "";
  connectingOauth = false;
  oauthError: string | null = null;

  // ── Per-module ADO routing edits (moduleId → draft project name) ──────────
  mappingDrafts: Record<string, string> = {};
  savingMappingId: string | null = null;
  mappingError: string | null = null;
  /** The catalog could not be LOADED — distinct from "the catalog is empty",
   *  which the panel answers with "run the catalog sync". */
  mappingLoadError: string | null = null;

  // ── ADO projects, listed from the internal DevOps connection ──────────────
  /** Live from Azure via `devops-projects` for the ONE internal connection —
   *  the same connection work items are filed through. Held here, not read from the
   *  ServiceStore cache: `devops-projects` is a scoped live proxy (no local
   *  table) whose entity cache accumulates across every connection ever
   *  queried, so a computed over it would list other orgs' projects. The
   *  find() RESPONSE is the truth for one connection — the same exception
   *  ReposStore, PipelinesBoardStore and ReleasesStore make for these proxies. */
  adoProjects: AdoProjectOption[] = [];
  adoProjectsLoading = false;
  /** Why the list is empty when it is — no internal connection, several, or
   *  the proxy failed. Typing a project name still works; this says why the
   *  picker cannot help. */
  adoProjectsNote: string | null = null;
  /** The connection the list came from, for the caption. */
  adoConnectionName: string | null = null;

  /** The providers list could not be LOADED — distinct from "no providers
   *  configured yet", which the picker answers with a link to Admin. */
  providersLoadError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-settings` ServiceStore (singleton, id "1"). */
  private get settingsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("read-ai-settings") ?? this.rootStore.readAiSettingsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `module-catalog` ServiceStore — the per-module ADO routing
   *  editor writes `adoProject` there (the ONLY ADO target that exists). */
  private get moduleCatalogService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("module-catalog") ?? this.rootStore.moduleCatalogStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `llm-providers` ServiceStore (masked find — secrets are never returned). */
  private get providersService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("llm-providers") ?? this.rootStore.llmProvidersStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `devops-connections` ServiceStore. */
  private get connectionsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `devops-projects` live proxy (find needs a connectionId). */
  private get projectsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("devops-projects") ?? this.rootStore.devopsProjectsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  /** The registered `read-ai-oauth` service (admin connect flow). */
  private get oauthService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore("read-ai-oauth") as unknown as ServiceStoreType | null) ?? null
    );
  }

  /**
   * The RAW Feathers service, for writes. The ServiceStore wrappers SWALLOW
   * server errors — they record `errorOnCreate` / `errorOnPatch` and resolve
   * null — which would turn every refusal the API composes ("state mismatch",
   * "already connected, pass force", "codes expire") into a generic message,
   * and a refused mapping patch into a field that silently snaps back. Same
   * trap ChangePasswordStore documents. Null when the app is not mounted
   * (tests); the callers then fall back to the wrapper and read its recorded
   * error instead of trusting a null result.
   */
  private rawService(path: string): RawWriteService | null {
    const app = this.rootStore.app as { service?: (p: string) => RawWriteService } | undefined;
    return app?.service?.(path) ?? null;
  }

  /**
   * A swallowed find's failure, named for the picker note. A sign-out's
   * `SessionEndedError` is returned untouched (see `swallowedFailure`), so the
   * catch still recognises it; wrapping it in the prefix would not.
   */
  private static listFailure(store: unknown, what: string): Error {
    const failure = swallowedFailure(store, "errorOnFind", "the request failed");
    return isSessionEnded(failure)
      ? failure
      : new Error(`Could not list ${what}: ${failure.message}`);
  }

  /**
   * The providers configured under Admin → LLM Providers, for the picker. A
   * computed view over the `llm-providers` cache (populated by load()). Every
   * live row is listed — a disabled one too, labelled, so an operator who
   * picked it and then disabled it can see why the chip went amber.
   */
  get providerOptions(): LlmProviderOption[] {
    const rows = (this.providersService?.entities ?? []) as unknown as Array<{
      id: string;
      name: string;
      type: string;
      enabled?: boolean | number;
      defaultModel?: string | null;
      deletedAt?: string | null;
    }>;
    return rows
      .filter((r) => r && r.id && !r.deletedAt)
      .map((r) => ({
        id: String(r.id),
        name: r.name || String(r.id),
        type: r.type,
        enabled: r.enabled === true || r.enabled === 1,
        defaultModel: r.defaultModel ?? null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** The picker's single value: `row:<id>` for a configured row, else the vendor preference. */
  get providerSelection(): string {
    return this.form.llmProviderId ? `${ROW_PREFIX}${this.form.llmProviderId}` : this.form.llmProvider || "auto";
  }

  /** The chosen provider row, when the picker names one and the cache knows it. */
  get selectedProvider(): LlmProviderOption | null {
    if (!this.form.llmProviderId) return null;
    return this.providerOptions.find((p) => p.id === this.form.llmProviderId) ?? null;
  }

  /**
   * One control for two fields. A row choice sets `llmProviderId` and aligns
   * the vendor preference with the row's vendor (so the API's vendor pin and
   * this page's per-vendor fields agree); a vendor choice clears the row.
   */
  setProviderSelection(value: string) {
    if (value.startsWith(ROW_PREFIX)) {
      const id = value.slice(ROW_PREFIX.length);
      const row = this.providerOptions.find((p) => p.id === id);
      const vendor = row && VENDOR_PREFERENCES.has(row.type) ? row.type : "auto";
      this.form = { ...this.form, llmProviderId: id, llmProvider: vendor };
      return;
    }
    this.form = { ...this.form, llmProviderId: "", llmProvider: value || "auto" };
  }

  /**
   * The saved record's `llmProviderIssue`, but ONLY while the picker still
   * names the row it was reported for. A draft that moved to another row has
   * no verdict yet (the API reports on what is saved), so showing the old
   * row's problem beside the new choice would be the wrong warning.
   */
  get providerIssueForSelection(): string | null {
    const chosen = this.form.llmProviderId;
    if (!chosen) return null;
    if (this.settings?.llmProviderId !== chosen) return null;
    return this.settings?.llmProviderIssue ?? null;
  }

  /** True when the picker names a row that is not the saved one yet. */
  get providerChoiceUnsaved(): boolean {
    return (this.form.llmProviderId || "") !== (this.settings?.llmProviderId || "");
  }

  /** Project names for the per-module picker, sorted. */
  get adoProjectOptions(): string[] {
    return this.adoProjects.map((p) => p.name).sort((a, b) => a.localeCompare(b));
  }

  /** Assignable governed modules, for the ADO routing editor. */
  get moduleMappings(): ModuleAdoMapping[] {
    const rows = (this.moduleCatalogService?.entities ?? []) as unknown as Array<
      ModuleAdoMapping & { assignable?: boolean; deletedAt?: string | null }
    >;
    return rows
      .filter((r) => r.assignable !== false && !r.deletedAt)
      .map((r) => ({
        id: r.id,
        moduleKey: r.moduleKey,
        displayName: r.displayName,
        adoProject: r.adoProject ?? null,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  /** Full URL Read.ai should POST to (built from the API origin + webhook path). */
  get webhookUrl(): string {
    const base = (env.apiUrl || "").replace(/\/+$/, "");
    const path = this.settings?.webhookPath || "/read-ai-webhook";
    return `${base}${path}`;
  }

  /** The HTTP header Read.ai signs deliveries with (shown in the setup docs). */
  get webhookSignatureHeader(): string {
    return this.settings?.webhookSignatureHeader || "X-Read-Signature";
  }

  get llmConfigured(): boolean {
    return !!this.settings?.llmConfigured;
  }

  /** True when Gantry's producer analyses meetings and HQ only attributes them.
   *  Absent (an older API) reads as false: the page then describes the pipeline
   *  that API actually runs. */
  get gantryAnalyses(): boolean {
    return this.settings?.analysisAuthority === "gantry";
  }

  setField(field: keyof ReadAiSettingsForm, value: string) {
    this.form = { ...this.form, [field]: value };
  }

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    this.testResult = null;
    this.webhookTestResult = null;
    try {
      // The connections find populates the devops-connections cache (read by
      // the `connections` getter); the settings get returns the singleton record.
      const [record, catalogResult, , providersResult] = await Promise.all([
        this.settingsService?.get("1") as unknown as Promise<
          ReadAiSettingsRecord | undefined
        >,
        // Populates the module-catalog cache the routing editor reads.
        this.moduleCatalogService?.find({ query: { $limit: 500 } }),
        this.loadOauthStatus(),
        // Populates the llm-providers cache the provider picker reads.
        this.providersService?.find({ query: { $limit: 200 } }),
        this.loadAdoProjects(),
      ]);
      // The ServiceStore's get SWALLOWS a failure into undefined (no
      // throwOnError is set). Seeding the form from nothing would render a
      // blank, editable page whose Save writes the blanks back — clearing the
      // provider pin and the models. A missing record is a load failure.
      // The other reads swallow a sign-out into null, so the Promise.all can
      // still resolve with the signed-out person's settings record.
      if (ended()) return;
      if (record == null) {
        throw swallowedFailure(this.settingsService, "errorOnGet", "Settings could not be loaded");
      }
      runInAction(() => {
        this.settings = record;
        // The ServiceStore's find SWALLOWS a failed load into an empty cache;
        // read its recorded error so an outage is not presented as "no
        // modules yet". Gated on the RESULT, because errorOnFind is sticky on
        // the shared store (CommandCenterStore does the same): a failure on
        // some earlier catalog find elsewhere must not hide the editor when
        // this load succeeded.
        this.mappingLoadError = catalogResult
          ? null
          : swallowedMessage(this.moduleCatalogService, "errorOnFind");
        // Same swallow, same gate on the RESULT: an outage must not be shown
        // as "no providers configured yet" beside a link to go add one.
        this.providersLoadError = providersResult
          ? null
          : swallowedMessage(this.providersService, "errorOnFind");
        // Seed editable (non-secret) fields from the record. Secrets stay blank.
        this.form = {
          llmProvider: record?.llmProvider || "auto",
          llmProviderId: record?.llmProviderId || "",
          anthropicApiKey: "",
          anthropicModel: record?.anthropicModel || "",
          geminiApiKey: "",
          geminiModel: record?.geminiModel || "",
          maxTokens: record?.maxTokens != null ? String(record.maxTokens) : "",
          webhookSecret: "",
          defaultWorkItemType: record?.defaultWorkItemType || "",
          autoApproveThreshold:
            record?.autoApproveThreshold != null
              ? String(record.autoApproveThreshold)
              : "",
        };
      });
    } catch (err: unknown) {
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load settings";
      });
      console.error("Failed to load read-ai settings:", err);
    } finally {
      // Signed out meanwhile: reset() cleared it, and a load the next person
      // started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  async save(): Promise<boolean> {
    // Never write a form that was never seeded: with no loaded record every
    // field is blank, and blanks are sent as nulls.
    if (!this.settings) {
      this.formError = "Settings did not load, so nothing was saved. Reload the page and try again.";
      return false;
    }
    const ended = sessionMark(this.rootStore);
    this.saving = true;
    this.formError = null;
    this.savedAt = null;
    try {
      const payload: Record<string, unknown> = {
        llmProvider: this.form.llmProvider || "auto",
        llmProviderId: this.form.llmProviderId || null,
        anthropicModel: this.form.anthropicModel.trim() || null,
        geminiModel: this.form.geminiModel.trim() || null,
        maxTokens: this.form.maxTokens.trim() === "" ? null : Number(this.form.maxTokens),
        defaultWorkItemType: this.form.defaultWorkItemType.trim() || null,
        autoApproveThreshold:
          this.form.autoApproveThreshold.trim() === ""
            ? null
            : Number(this.form.autoApproveThreshold),
      };
      // Write-only secrets: only send when the admin actually typed a new value.
      if (this.form.anthropicApiKey.trim()) {
        payload.anthropicApiKey = this.form.anthropicApiKey.trim();
      }
      if (this.form.geminiApiKey.trim()) {
        payload.geminiApiKey = this.form.geminiApiKey.trim();
      }
      if (this.form.webhookSecret.trim()) {
        payload.webhookSecret = this.form.webhookSecret.trim();
      }

      // The raw service, so a refusal (an llmProviderId the API will not accept,
      // a bad threshold) REJECTS here instead of being swallowed into a null
      // that this used to record as a successful save — same trap saveMapping
      // documents.
      const raw = this.rawService("read-ai-settings");
      const record = (raw
        ? await raw.patch("1", payload)
        : await this.settingsService?.patch("1", payload)) as ReadAiSettingsRecord | null | undefined;
      // The ServiceStore path resolves null when a sign-out ended the call:
      // neither "did not confirm" nor the old person's record belongs to the
      // next one's form.
      if (ended()) return false;
      if (record == null) {
        throw swallowedFailure(this.settingsService, "errorOnPatch", "The server did not confirm the save");
      }
      runInAction(() => {
        this.settings = record;
        // Clear the write-only secret fields so they never linger, and re-seed
        // the non-secret fields from the saved record.
        this.form = {
          ...this.form,
          anthropicApiKey: "",
          geminiApiKey: "",
          webhookSecret: "",
          llmProvider: record?.llmProvider || "auto",
          llmProviderId: record?.llmProviderId || "",
          anthropicModel: record?.anthropicModel || "",
          geminiModel: record?.geminiModel || "",
          maxTokens: record?.maxTokens != null ? String(record.maxTokens) : "",
          defaultWorkItemType: record?.defaultWorkItemType || "",
          autoApproveThreshold:
            record?.autoApproveThreshold != null
              ? String(record.autoApproveThreshold)
              : "",
        };
        this.savedAt = new Date().toLocaleTimeString();
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.formError = err instanceof Error ? err.message : "Failed to save settings";
      });
      return false;
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and a save the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.saving = false;
        });
      }
    }
  }

  /** Run the API's LLM connectivity test (a tiny ping). No secret is sent. */
  async test(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.testing = true;
    this.testResult = null;
    try {
      // Raw service, as save(): a refusal rejects with the server's message
      // instead of being swallowed into a null that reads "No result returned".
      const raw = this.rawService("read-ai-settings");
      const payload = { action: "test" };
      const record = (raw
        ? await raw.patch("1", payload)
        : await this.settingsService?.patch("1", payload)) as ReadAiSettingsRecord | null | undefined;
      if (ended()) return;
      if (record == null) {
        throw swallowedFailure(this.settingsService, "errorOnPatch", "No result returned");
      }
      runInAction(() => {
        this.settings = record;
        this.testResult = record.test ?? { ok: false, message: "No result returned" };
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.testResult = {
            ok: false,
            message: err instanceof Error ? err.message : "LLM test failed",
          };
        }
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.testing = false;
        });
      }
    }
  }

  /**
   * Run the API's webhook self-test: it signs a synthetic payload with the stored
   * signing key and replays it through the webhook in dry-run mode, verifying the
   * HMAC end-to-end without creating a meeting. No secret is sent or returned.
   */
  async testWebhook(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.testingWebhook = true;
    this.webhookTestResult = null;
    try {
      const raw = this.rawService("read-ai-settings");
      const payload = { action: "testWebhook" };
      const record = (raw
        ? await raw.patch("1", payload)
        : await this.settingsService?.patch("1", payload)) as ReadAiSettingsRecord | null | undefined;
      if (ended()) return;
      if (record == null) {
        throw swallowedFailure(this.settingsService, "errorOnPatch", "No result returned");
      }
      runInAction(() => {
        this.settings = record;
        this.webhookTestResult = record.webhookTest ?? { ok: false, message: "No result returned" };
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.webhookTestResult = {
            ok: false,
            message: err instanceof Error ? err.message : "Webhook test failed",
          };
        }
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.testingWebhook = false;
        });
      }
    }
  }

  // ── Read.ai OAuth actions ─────────────────────────────────────────────────

  async loadOauthStatus(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    try {
      const result = (await this.oauthService?.find({})) as unknown;
      // The ServiceStore (throwOnError false) SWALLOWS a sign-out into a null
      // result, so the catch below never sees it; writing that null would blank
      // the status card the next person's own load just filled.
      if (ended()) return;
      const status = (Array.isArray(result) ? result[0] : (result as { data?: unknown[] })?.data?.[0] ?? result) as
        | ReadAiOauthStatus
        | undefined;
      runInAction(() => {
        this.oauthStatus = status && typeof status.connected === "boolean" ? status : null;
      });
    } catch (err) {
      if (isSessionEnded(err) || ended()) return;
      // The status card renders "unknown" rather than a fake disconnected.
      runInAction(() => {
        this.oauthStatus = null;
      });
    }
  }

  /** Step 1: mint the authorize URL (force restarts over a healthy chain). */
  async startOauth(force = false): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.connectingOauth = true;
    this.oauthError = null;
    try {
      const payload = { action: "start", force };
      const raw = this.rawService("read-ai-oauth");
      const result = (raw
        ? await raw.create(payload)
        : await this.oauthService?.create(payload as never)) as unknown as { authorizeUrl?: string } | undefined;
      if (ended()) return;
      if (!result?.authorizeUrl) {
        throw swallowedFailure(this.oauthService, "errorOnCreate", "The server returned no authorize URL");
      }
      runInAction(() => {
        this.authorizeUrl = result.authorizeUrl!;
        this.finishUrl = "";
      });
      await this.loadOauthStatus();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.oauthError = err instanceof Error ? err.message : "Failed to start the connection";
      });
    } finally {
      // connectingOauth is shared with finishOauth: a sign-out cleared it, and
      // the next person's own connect may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.connectingOauth = false;
        });
      }
    }
  }

  /** Step 2: exchange the pasted redirect URL for the stored connection. */
  async finishOauth(): Promise<boolean> {
    if (!this.finishUrl.trim()) return false;
    const ended = sessionMark(this.rootStore);
    this.connectingOauth = true;
    this.oauthError = null;
    try {
      const payload = { action: "finish", redirectedUrl: this.finishUrl.trim() };
      const raw = this.rawService("read-ai-oauth");
      const result = (raw
        ? await raw.create(payload)
        : await this.oauthService?.create(payload as never)) as unknown as { connected?: boolean } | undefined;
      if (ended()) return false;
      if (result?.connected !== true) {
        throw swallowedFailure(
          this.oauthService,
          "errorOnCreate",
          "The server did not confirm the connection"
        );
      }
      runInAction(() => {
        this.authorizeUrl = null;
        this.finishUrl = "";
      });
      await this.loadOauthStatus();
      // The status read swallows a sign-out; the toast is not the next person's.
      if (ended()) return false;
      this.rootStore.notificationStore?.addSuccessNotification(
        "Connected to Read.ai — transcript and recording fetches are now available."
      );
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) this.oauthError = err instanceof Error ? err.message : "Failed to finish the connection";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.connectingOauth = false;
        });
      }
    }
  }

  setFinishUrl(value: string) {
    this.finishUrl = value;
  }

  // ── ADO projects from the internal DevOps connection ──────────────────────

  /**
   * List the projects Azure reports for the ONE internal DevOps connection
   * (`clientId` null) — the rule the accept path uses to pick the credential it
   * files through, applied here so the picker offers exactly the projects an
   * the accept path could reach. Zero or several internal connections is
   * reported, not guessed around: the accept path defers for the same reason, and the
   * message names it. Best-effort — a failure leaves the free-text path open.
   */
  async loadAdoProjects(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.adoProjectsLoading = true;
    this.adoProjectsNote = null;
    try {
      // `clientId: null` is the API's own filter for the internal org (a
      // whereNull), sent over the socket transport where null survives; the
      // client-side filter below is belt-and-braces. $limit is the service's
      // ceiling, so the internal row cannot fall off a newest-first page.
      const result = await this.connectionsService?.find({ query: { clientId: null, $limit: 500 } });
      // A sign-out is swallowed into null (and parked on errorOnFind, which the
      // next person's find clears): stop before reading either.
      if (ended()) return;
      if (result == null) {
        // The wrapper SWALLOWS a failed find into null — read its recorded
        // error, or this outage would be presented as "no internal connection".
        throw ReadAiSettingsStore.listFailure(this.connectionsService, "DevOps connections");
      }
      const connections = rowsOf<{
        id: string;
        name?: string;
        clientId?: string | null;
        deletedAt?: string | null;
      }>(result).filter((c) => c && c.id && (c.clientId ?? null) === null && !c.deletedAt);
      if (connections.length === 0) {
        runInAction(() => {
          this.adoProjects = [];
          this.adoConnectionName = null;
          this.adoProjectsNote =
            "No internal DevOps connection exists, so projects cannot be listed (and accepting an item could file no work item). Add one under DevOps → Connections, or type the project name.";
        });
        return;
      }
      if (connections.length > 1) {
        runInAction(() => {
          this.adoProjects = [];
          this.adoConnectionName = null;
          this.adoProjectsNote =
            `${connections.length} internal DevOps connections exist (${connections
              .map((c) => c.name || c.id)
              .join(", ")}); work-item delivery needs exactly one. Retire the extras to list projects here, or type the project name.`;
        });
        return;
      }
      const conn = connections[0];
      const projectsResult = await this.projectsService?.find({ query: { connectionId: conn.id } });
      if (ended()) return;
      if (projectsResult == null) {
        // Same swallow: an expired PAT resolves null with errorOnFind, and must
        // not read as "Azure reported no projects".
        throw ReadAiSettingsStore.listFailure(this.projectsService, "ADO projects");
      }
      const projects = rowsOf<{ id?: string; name?: string }>(projectsResult)
        .filter((p) => p && typeof p.name === "string" && p.name.trim())
        .map((p) => ({ id: String(p.id ?? p.name), name: String(p.name).trim() }));
      runInAction(() => {
        this.adoProjects = projects;
        this.adoConnectionName = conn.name || conn.id;
        this.adoProjectsNote =
          projects.length === 0
            ? `Azure reported no projects for connection "${conn.name || conn.id}". Type the project name if you know it.`
            : null;
      });
    } catch (err: unknown) {
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.adoProjects = [];
        this.adoConnectionName = null;
        const message = err instanceof Error ? err.message : "unknown error";
        this.adoProjectsNote = `${
          message.startsWith("Could not list") ? message : `Could not list ADO projects: ${message}`
        }. You can still type the project name.`;
      });
    } finally {
      // Signed out meanwhile: reset() cleared the flag, and a load the next
      // person started may be the one in flight now.
      if (!ended()) {
        runInAction(() => {
          this.adoProjectsLoading = false;
        });
      }
    }
  }

  // ── Per-module ADO routing actions ────────────────────────────────────────

  setMappingDraft(id: string | number, value: string) {
    this.mappingDrafts = { ...this.mappingDrafts, [String(id)]: value };
  }

  /** The draft if the admin typed one, else the stored mapping. */
  mappingValue(row: ModuleAdoMapping): string {
    const draft = this.mappingDrafts[String(row.id)];
    return draft !== undefined ? draft : row.adoProject ?? "";
  }

  async saveMapping(row: ModuleAdoMapping): Promise<void> {
    const value = this.mappingValue(row).trim();
    if ((row.adoProject ?? "") === value) return; // unchanged — no write
    // A pick from the list saves at once and the field then blurs; the blur
    // must not fire a second patch for the same value while the first is in
    // flight.
    if (this.savingMappingId === String(row.id)) return;
    const ended = sessionMark(this.rootStore);
    this.savingMappingId = String(row.id);
    this.mappingError = null;
    try {
      const data = { adoProject: value || null };
      const raw = this.rawService("module-catalog");
      const result = raw
        ? await raw.patch(String(row.id), data)
        : await this.moduleCatalogService?.patch(String(row.id), data as never);
      if (ended()) return;
      if (result == null) {
        // A null result is a SWALLOWED refusal, not a save: keep the draft so
        // the admin's edit is still on screen with the reason beside it.
        throw swallowedFailure(
          this.moduleCatalogService,
          "errorOnPatch",
          `The server did not confirm the mapping for ${row.displayName}`
        );
      }
      if (raw) {
        // The raw write bypassed the ServiceStore's cache — refresh the row so
        // moduleMappings (a computed over that cache) shows what was saved.
        await this.moduleCatalogService?.get?.(String(row.id));
        // That get is a ServiceStore call: a sign-out is swallowed into undefined.
        if (ended()) return;
      }
      runInAction(() => {
        const drafts = { ...this.mappingDrafts };
        delete drafts[String(row.id)];
        this.mappingDrafts = drafts;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err) && !ended()) {
          this.mappingError =
            err instanceof Error ? err.message : `Failed to save the mapping for ${row.displayName}`;
        }
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.savingMappingId = null;
        });
      }
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.settings = null;
    this.form = { ...EMPTY_FORM };
    this.loading = false;
    this.saving = false;
    this.testing = false;
    this.testingWebhook = false;
    this.error = null;
    this.formError = null;
    this.savedAt = null;
    this.testResult = null;
    this.webhookTestResult = null;
    this.oauthStatus = null;
    this.authorizeUrl = null;
    this.finishUrl = "";
    this.connectingOauth = false;
    this.oauthError = null;
    this.mappingDrafts = {};
    this.savingMappingId = null;
    this.mappingError = null;
    this.mappingLoadError = null;
    this.adoProjects = [];
    this.adoProjectsLoading = false;
    this.adoProjectsNote = null;
    this.adoConnectionName = null;
    this.providersLoadError = null;
  }
}

export default ReadAiSettingsStore;
